import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Plus, Trash2, Edit3, X, Download, FileText,
         Import, RefreshCw, Search } from "lucide-react";

const STATUS_OPTIONS = [
  { id: "veuve",    label: "Veuve",    color: "bg-purple-100 text-purple-800" },
  { id: "orphelin", label: "Orphelin", color: "bg-amber-100 text-amber-800"  },
  { id: "autre",    label: "Autre",    color: "bg-gray-100 text-gray-800"    },
];

const NEEDS_OPTIONS = [
  "Scolarité enfants", "Santé", "Alimentation", "Logement / loyer",
  "Reprise d'activité", "Formation professionnelle",
  "Accompagnement psychologique", "Autre",
];

const emptyForm = {
  first_name: "", last_name: "", sex: "F", birth_date: "", birth_place: "",
  nationality: "Togolaise", id_document: "", address: "", phone: "",
  status: "veuve", matrimonial_status: "", widowing_date: "",
  deceased_relative_name: "", children_count: 0, children_scolarized: 0,
  profession: "", workplace: "", monthly_income_fcfa: "",
  main_charges: "", other_aid: "", needs: [], free_notes: "",
  consent_gdpr: true, registered_by: "",
};

export default function BeneficiariesAdminPanel({ API }) {
  const pin = "SAFUAYE2024";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState({});   // {id: true}
  const [pvParams, setPvParams] = useState({
    meeting_date: "2026-09-12",
    meeting_place: "Siège SAFUAYE-TOGO · Atakpamé",
    donation_amount_fcfa: 15000,
    kit_amount_per_beneficiary_fcfa: 5000,
    treasurer_name: "Trésorier SAFUAYE-TOGO",
    president_national_name: "M. AZIAKA Koffi",
    president_international_name: "M. APEDO Kokou Ognakotan",
  });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/beneficiaries`, { params: { pin } });
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setShowForm(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      ...emptyForm, ...b,
      monthly_income_fcfa: b.monthly_income_fcfa ?? "",
      needs: b.needs || [],
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const submitForm = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      alert("Nom et prénoms sont obligatoires."); return;
    }
    setBusy("save");
    try {
      const payload = {
        ...form, pin,
        monthly_income_fcfa: form.monthly_income_fcfa
          ? Number(form.monthly_income_fcfa) : null,
        children_count: Number(form.children_count) || 0,
        children_scolarized: Number(form.children_scolarized) || 0,
      };
      if (editing) {
        await axios.put(`${API}/beneficiaries/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/beneficiaries`, payload);
      }
      await load(); closeForm();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const removeBeneficiary = async (b) => {
    if (!window.confirm(`Retirer ${b.last_name} ${b.first_name} ?`)) return;
    setBusy(`del-${b.id}`);
    try {
      await axios.delete(`${API}/beneficiaries/${b.id}`, { params: { pin } });
      await load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const importFromGrants = async () => {
    if (!window.confirm("Importer les 7 bénéficiaires connus (Juin 2026) dans le registre ?")) return;
    setBusy("import");
    try {
      const r = await axios.post(`${API}/beneficiaries/import-from-grants`, null,
        { params: { pin } });
      alert(`✅ Import terminé : ${r.data.inserted} bénéficiaires ajoutés (${r.data.skipped} déjà présents).`);
      await load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const exportCsv = () => {
    window.open(`${API}/beneficiaries/export.csv?pin=${pin}`, "_blank");
  };

  const toggleSelect = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectAll = () => {
    const next = {};
    filtered.forEach(b => { next[b.id] = true; });
    setSelected(next);
  };
  const clearSelection = () => setSelected({});

  const generatePV = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) { alert("Sélectionnez au moins 1 bénéficiaire."); return; }
    setBusy("pv");
    try {
      const r = await axios.post(`${API}/beneficiaries/generate-pv-remise`, {
        pin, beneficiary_ids: ids,
        meeting_date: pvParams.meeting_date,
        meeting_place: pvParams.meeting_place,
        donation_amount_fcfa: Number(pvParams.donation_amount_fcfa),
        kit_amount_per_beneficiary_fcfa: Number(pvParams.kit_amount_per_beneficiary_fcfa),
        treasurer_name: pvParams.treasurer_name,
        president_national_name: pvParams.president_national_name,
        president_international_name: pvParams.president_international_name,
      });
      window.open(r.data.url, "_blank");
      alert(`✅ PV généré (${r.data.beneficiary_count} bénéficiaires, ${r.data.size_kb} Ko).`);
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  const filtered = items.filter(b => {
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (b.last_name || "").toLowerCase().includes(s)
        || (b.first_name || "").toLowerCase().includes(s)
        || (b.phone || "").toLowerCase().includes(s)
        || (b.profession || "").toLowerCase().includes(s);
  });
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="bg-gradient-to-br from-purple-50 via-amber-50 to-emerald-50 border-2 border-purple-300 rounded-2xl p-5 mb-6" data-testid="beneficiaries-admin-panel">
      <div className="flex items-start gap-3 mb-4">
        <span className="text-3xl">👥</span>
        <div className="flex-1">
          <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 flex-wrap">
            Registre des Bénéficiaires SAFUAYE-TOGO
            <span className="text-xs bg-purple-700 text-white px-2 py-0.5 rounded font-semibold">🆕 Sept. 2026</span>
          </h4>
          <p className="text-xs text-gray-600 mt-1">
            Rencontre du 12 septembre 2026 · Saisir les fiches collectées, exporter en CSV et générer le procès-verbal de remise des dons signable.
          </p>
        </div>
      </div>

      {/* Actions barre */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={openCreate}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-2"
                data-testid="beneficiaries-add-btn">
          <Plus className="w-4 h-4" /> Nouveau bénéficiaire
        </button>
        <button onClick={importFromGrants} disabled={busy === "import"}
                className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-2"
                data-testid="beneficiaries-import-btn">
          <Import className="w-4 h-4" /> {busy === "import" ? "Import…" : "Importer 7 (Juin 2026)"}
        </button>
        <button onClick={exportCsv}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-semibold flex items-center gap-2"
                data-testid="beneficiaries-export-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
        <button onClick={load}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded text-sm font-semibold flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </button>
      </div>

      {/* Filtre + recherche */}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Rechercher un nom, téléphone, profession…"
                 className="pl-8 pr-3 py-2 border border-gray-300 rounded w-full text-sm"
                 data-testid="beneficiaries-search-input" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
                data-testid="beneficiaries-status-filter">
          <option value="all">Tous statuts</option>
          {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <span className="text-xs text-gray-600 whitespace-nowrap">
          {filtered.length} / {items.length}
        </span>
      </div>

      {/* Liste des bénéficiaires */}
      <div className="bg-white rounded-lg border border-gray-200 mb-4 max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="p-2 text-left">
                <input type="checkbox"
                       checked={filtered.length > 0 && filtered.every(b => selected[b.id])}
                       onChange={e => e.target.checked ? selectAll() : clearSelection()}
                       data-testid="beneficiaries-select-all" />
              </th>
              <th className="p-2 text-left">Nom & Prénoms</th>
              <th className="p-2 text-left">Statut</th>
              <th className="p-2 text-left">Téléphone</th>
              <th className="p-2 text-left">Enfants</th>
              <th className="p-2 text-left">Profession</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-gray-500 text-sm">
                Aucun bénéficiaire enregistré. Cliquez sur "Nouveau bénéficiaire" ou "Importer 7 (Juin 2026)".
              </td></tr>
            )}
            {filtered.map(b => {
              const status = STATUS_OPTIONS.find(s => s.id === b.status) || STATUS_OPTIONS[2];
              return (
                <tr key={b.id} className="border-t border-gray-100 hover:bg-purple-50">
                  <td className="p-2">
                    <input type="checkbox" checked={!!selected[b.id]}
                           onChange={() => toggleSelect(b.id)}
                           data-testid={`beneficiary-select-${b.id}`} />
                  </td>
                  <td className="p-2 font-medium text-gray-900">
                    {b.last_name} {b.first_name}
                  </td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="p-2 text-gray-700">{b.phone || "—"}</td>
                  <td className="p-2 text-gray-700 text-center">
                    {b.children_count || 0}
                    {b.children_scolarized ? ` (${b.children_scolarized} scol.)` : ""}
                  </td>
                  <td className="p-2 text-gray-700 truncate max-w-[200px]">
                    {b.profession || "—"}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(b)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                            data-testid={`beneficiary-edit-${b.id}`}>
                      <Edit3 className="w-4 h-4 inline" />
                    </button>
                    <button onClick={() => removeBeneficiary(b)}
                            disabled={busy === `del-${b.id}`}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`beneficiary-delete-${b.id}`}>
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bloc PV auto */}
      <div className="bg-white border-2 border-emerald-400 rounded-lg p-4">
        <h5 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2 mb-2">
          <FileText className="w-5 h-5 text-emerald-600" />
          Générer le PV de remise des dons (12 sept 2026)
        </h5>
        <p className="text-xs text-gray-600 mb-3">
          Sélectionnez les bénéficiaires ci-dessus qui recevront le don, puis générez le PV signable à imprimer pour la rencontre.
          <span className="ml-1 font-semibold text-emerald-700">{selectedCount} sélectionné(s).</span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-3 text-xs">
          <label className="flex flex-col">
            <span className="text-gray-600">Date rencontre</span>
            <input type="date" value={pvParams.meeting_date}
                   onChange={e => setPvParams({ ...pvParams, meeting_date: e.target.value })}
                   className="px-2 py-1 border border-gray-300 rounded"
                   data-testid="pv-meeting-date" />
          </label>
          <label className="flex flex-col">
            <span className="text-gray-600">Lieu</span>
            <input type="text" value={pvParams.meeting_place}
                   onChange={e => setPvParams({ ...pvParams, meeting_place: e.target.value })}
                   className="px-2 py-1 border border-gray-300 rounded" />
          </label>
          <label className="flex flex-col">
            <span className="text-gray-600">Don individuel (FCFA)</span>
            <input type="number" value={pvParams.donation_amount_fcfa}
                   onChange={e => setPvParams({ ...pvParams, donation_amount_fcfa: e.target.value })}
                   className="px-2 py-1 border border-gray-300 rounded" />
          </label>
          <label className="flex flex-col">
            <span className="text-gray-600">Kit / bénéf. (FCFA)</span>
            <input type="number" value={pvParams.kit_amount_per_beneficiary_fcfa}
                   onChange={e => setPvParams({ ...pvParams, kit_amount_per_beneficiary_fcfa: e.target.value })}
                   className="px-2 py-1 border border-gray-300 rounded" />
          </label>
          <label className="flex flex-col">
            <span className="text-gray-600">Trésorier</span>
            <input type="text" value={pvParams.treasurer_name}
                   onChange={e => setPvParams({ ...pvParams, treasurer_name: e.target.value })}
                   className="px-2 py-1 border border-gray-300 rounded" />
          </label>
          <label className="flex flex-col">
            <span className="text-gray-600">Président National</span>
            <input type="text" value={pvParams.president_national_name}
                   onChange={e => setPvParams({ ...pvParams, president_national_name: e.target.value })}
                   className="px-2 py-1 border border-gray-300 rounded" />
          </label>
        </div>
        <button onClick={generatePV} disabled={busy === "pv" || selectedCount === 0}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"
                data-testid="pv-generate-btn">
          <FileText className="w-4 h-4" />
          {busy === "pv" ? "Génération…" : `Générer PV (${selectedCount} bénéficiaire${selectedCount > 1 ? "s" : ""})`}
        </button>
      </div>

      {/* Modal formulaire */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             data-testid="beneficiary-form-modal">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? "Modifier le bénéficiaire" : "Nouveau bénéficiaire"}
              </h3>
              <button onClick={closeForm}
                      className="text-gray-500 hover:text-gray-800"
                      data-testid="beneficiary-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {/* Identité */}
              <div className="md:col-span-2 font-semibold text-purple-700 border-b border-purple-200 pb-1">
                1. Identité
              </div>
              <label className="flex flex-col">
                <span className="text-gray-600">Nom *</span>
                <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded"
                       data-testid="beneficiary-form-lastname" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Prénoms *</span>
                <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded"
                       data-testid="beneficiary-form-firstname" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Sexe</span>
                <select value={form.sex} onChange={e => setForm({ ...form, sex: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded">
                  <option value="F">Féminin</option>
                  <option value="M">Masculin</option>
                </select>
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Date naissance</span>
                <input value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })}
                       placeholder="ex: 1965-05-12 ou vers 1965"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Lieu de naissance</span>
                <input value={form.birth_place} onChange={e => setForm({ ...form, birth_place: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Nationalité</span>
                <input value={form.nationality} onChange={e => setForm({ ...form, nationality: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Pièce d'identité (type + N°)</span>
                <input value={form.id_document} onChange={e => setForm({ ...form, id_document: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Adresse</span>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Téléphone</span>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Statut</span>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded">
                  {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>

              {/* Famille */}
              <div className="md:col-span-2 font-semibold text-purple-700 border-b border-purple-200 pb-1 mt-3">
                2. Situation familiale
              </div>
              <label className="flex flex-col">
                <span className="text-gray-600">Situation matrimoniale</span>
                <input value={form.matrimonial_status} onChange={e => setForm({ ...form, matrimonial_status: e.target.value })}
                       placeholder="Célibataire / Marié(e) / Veuve / Divorcé(e)"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Date de veuvage</span>
                <input value={form.widowing_date} onChange={e => setForm({ ...form, widowing_date: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Nom du conjoint défunt / père-mère décédé(e)</span>
                <input value={form.deceased_relative_name} onChange={e => setForm({ ...form, deceased_relative_name: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Enfants à charge (total)</span>
                <input type="number" min="0" value={form.children_count} onChange={e => setForm({ ...form, children_count: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Enfants scolarisés</span>
                <input type="number" min="0" value={form.children_scolarized} onChange={e => setForm({ ...form, children_scolarized: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>

              {/* Situation pro */}
              <div className="md:col-span-2 font-semibold text-purple-700 border-b border-purple-200 pb-1 mt-3">
                3. Situation professionnelle et économique
              </div>
              <label className="flex flex-col">
                <span className="text-gray-600">Activité principale</span>
                <input value={form.profession} onChange={e => setForm({ ...form, profession: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Lieu d'exercice</span>
                <input value={form.workplace} onChange={e => setForm({ ...form, workplace: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Revenu mensuel estimé (FCFA)</span>
                <input type="number" min="0" value={form.monthly_income_fcfa}
                       onChange={e => setForm({ ...form, monthly_income_fcfa: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col">
                <span className="text-gray-600">Autre aide reçue</span>
                <input value={form.other_aid} onChange={e => setForm({ ...form, other_aid: e.target.value })}
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Charges principales</span>
                <input value={form.main_charges} onChange={e => setForm({ ...form, main_charges: e.target.value })}
                       placeholder="Loyer · scolarité · santé…"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>

              {/* Besoins */}
              <div className="md:col-span-2 font-semibold text-purple-700 border-b border-purple-200 pb-1 mt-3">
                4. Besoins prioritaires
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-2">
                {NEEDS_OPTIONS.map(n => {
                  const active = form.needs.includes(n);
                  return (
                    <button key={n} type="button"
                            onClick={() => setForm({
                              ...form,
                              needs: active
                                ? form.needs.filter(x => x !== n)
                                : [...form.needs, n],
                            })}
                            className={`text-xs px-3 py-1 rounded-full border ${
                              active
                                ? "bg-purple-600 text-white border-purple-600"
                                : "bg-white text-gray-700 border-gray-300 hover:border-purple-400"
                            }`}>
                      {n}
                    </button>
                  );
                })}
              </div>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Notes libres du bénéficiaire</span>
                <textarea value={form.free_notes} onChange={e => setForm({ ...form, free_notes: e.target.value })}
                          rows={3}
                          className="px-2 py-1 border border-gray-300 rounded" />
              </label>

              <label className="flex items-center gap-2 md:col-span-2 text-xs text-gray-700">
                <input type="checkbox" checked={form.consent_gdpr}
                       onChange={e => setForm({ ...form, consent_gdpr: e.target.checked })} />
                Consentement RGPD : le bénéficiaire autorise SAFUAYE à conserver ses données de manière confidentielle et à les utiliser pour le suivi.
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-gray-600">Enregistré par</span>
                <input value={form.registered_by} onChange={e => setForm({ ...form, registered_by: e.target.value })}
                       placeholder="Nom du représentant SAFUAYE-TOGO"
                       className="px-2 py-1 border border-gray-300 rounded" />
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeForm}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded text-sm font-semibold">
                Annuler
              </button>
              <button onClick={submitForm} disabled={busy === "save"}
                      className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm font-semibold"
                      data-testid="beneficiary-form-save">
                {busy === "save" ? "Enregistrement…" : (editing ? "Mettre à jour" : "Enregistrer")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
